<script lang="ts" setup>
import type { PreparedAuctionItem } from '@autobid/ui/types/components/AuctionsData'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth.js'
import { useCatalog } from '@autobid/ui/composables/auction/useCatalog'
import { useCheckAccessToAuction } from '@autobid/ui/composables/useAccessToAuction'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { useMutation } from '@tanstack/vue-query'

const auction = defineProps<{
  catalog: PreparedAuctionItem['catalog']
  restrictions: PreparedAuctionItem['restrictions']
  appId: PreparedAuctionItem['appId']
  stage: PreparedAuctionItem['stage']
  type: PreparedAuctionItem['type']
}>()
const checkAccessToAuction = useCheckAccessToAuction()

const { isAuthed } = useAutobidAuth()

const userHasAccessToAuction = computed(() => {
  if (auction.type === 'LIVE') {
    return true
  }

  const accessData = checkAccessToAuction(auction.restrictions, auction.appId, isAuthed.value)

  return accessData.statusCode === 200
})
const userCanDownloadXLS = computed(() => {
  return Boolean(userHasAccessToAuction.value && isAuthed.value && auction.stage === 'ONLINE' && auction.catalog?.urlXLS)
})

const shouldRenderTrigger = computed(() => {
  if (auction.stage === 'IN_PREPARATION' && auction.catalog?.type === 'GENERATED') {
    return false
  }

  return (
    (userHasAccessToAuction.value && auction.catalog?.url) || userCanDownloadXLS.value
  )
})

const { handleCatalog } = useCatalog()

const { mutateAsync: fetchPdfCatalog, isLoading: isFetchingPdf } = useMutation({
  mutationFn: async () => await handleCatalog({ type: 'pdf', auction, mode: 'download' }),
})
const { mutateAsync: fetchXlsCatalog, isLoading: isFetchingXls } = useMutation({
  mutationFn: async () => await handleCatalog({ type: 'xls', auction, mode: 'download' }),
})
</script>

<template>
  <Popover v-if="shouldRenderTrigger" @click.stop>
    <PopoverButton as="template">
      <slot />
    </PopoverButton>

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0"
    >
      <PopoverPanel
        v-slot="{ close }"
        class="absolute z-50 w-48 bg-white shadow-md"
      >
        <header>
          <h3 class="mt-1 p-2">
            {{ $t('auction-catalog-popover.title') }}
          </h3>
          <button
            class="sm absolute right-2 top-2 flex text-black duration-300 hover:scale-110 hover:text-primary"
            @click="close"
          >
            <span class="sr-only">{{ $t('close') }} </span>
            <Icon mode="svg" name="ic:outline-close" />
          </button>
        </header>
        <div class="relative grid text-sm text-black" @click="close">
          <button
            v-if="auction.catalog?.url"
            class="flex items-center gap-2 p-2 hover:bg-slate-200"
            :disabled="isFetchingPdf"
            @click="() => fetchPdfCatalog()"
          >
            <Icon
              mode="svg"
              name="vscode-icons:file-type-pdf2"
              class="size-6"
            />
            {{ $t('auction-catalog-popover.pdf') }}
          </button>
          <button
            v-if="userCanDownloadXLS"
            class="flex items-center gap-2 p-2 hover:bg-slate-200"
            :disabled="isFetchingXls"
            @click="() => fetchXlsCatalog()"
          >
            <Icon
              mode="svg"
              name="vscode-icons:file-type-excel2"
              class="size-6"
            />
            {{ $t('auction-catalog-popover.xls') }}
          </button>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>
</template>
